import React, { useEffect, useRef, useState } from 'react'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import API from 'common/logic/API'
import { Button, Form, Spinner, Table } from 'react-bootstrap'
import { Formik } from 'formik'
import * as yup from 'yup'
import { Navigate } from 'react-router-dom'
import * as Types from 'common/utils/Types'
import { ConditionNode, LeafConditionType, Movement, Tag } from 'types'
import TagSelect from 'common/components/TagSelect'
import ConditionTreeEditor, { LeafNode, LeafRenderResult, TreeNode, validateTree } from 'common/components/ConditionTreeEditor'

interface Props {
    onSuccess: string | ((result: unknown) => void)
    static?: boolean
    entryId?: number
}

interface FormProps {
    loadedTags: Tag[]
    previousValues: any
    completion: (input: unknown) => void
}

const LEAF_TYPES: { value: LeafConditionType; label: string; placeholder: string }[] = [
    { value: 'MATCH_REGEX_PAYEE', label: 'Payee (regex)', placeholder: 'AMAZON' },
    { value: 'MATCH_REGEX_PAYMENT_REFERENCE', label: 'Payment reference (regex)', placeholder: 'INVOICE' },
    { value: 'EXACT_AMOUNT', label: 'Exact amount', placeholder: '42.50' },
    { value: 'GREATER_THAN_AMOUNT', label: 'Greater than', placeholder: '100' },
    { value: 'LESSER_THAN_AMOUNT', label: 'Less than', placeholder: '0' },
]

const defaultLeaf = (): LeafNode => ({ kind: 'leaf', type: 'MATCH_REGEX_PAYEE', parameter: '' })
const defaultTree = (): ConditionNode => ({ kind: 'all', children: [defaultLeaf() as unknown as ConditionNode] })

const isLeafValid = (leaf: LeafNode) => leaf.parameter.trim() !== ''

function renderLeaf(node: LeafNode, onChange: (n: LeafNode) => void, showErrors: boolean): LeafRenderResult {
    const info = LEAF_TYPES.find(t => t.value === node.type)
    const isInvalid = showErrors && node.parameter.trim() === ''

    const typeSelector = (
        <Form.Control as="select" size="sm" value={node.type}
            onChange={e => onChange({ ...node, type: e.target.value as LeafConditionType, parameter: '' } as LeafNode)}>
            {LEAF_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </Form.Control>
    )

    const parameterInput = (
        <Form.Control size="sm" type="text" placeholder={info?.placeholder ?? ''}
            value={node.parameter}
            onChange={e => onChange({ ...node, parameter: e.target.value })}
            isInvalid={isInvalid} />
    )

    return { typeSelector, parameterInput, isInvalid }
}

function ClassificationRuleForm({ loadedTags, previousValues, completion }: FormProps) {
    const initialTagIds: number[] = previousValues?.tags?.map((t: Tag) => t.id) ?? []
    const [tagIds, setTagIds] = useState<number[]>(initialTagIds)
    const [availableTags, setAvailableTags] = useState<Tag[]>(loadedTags)
    const [conditionTree, setConditionTree] = useState<ConditionNode>(
        previousValues?.condition ?? defaultTree()
    )
    const [showErrors, setShowErrors] = useState(false)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [previewResult, setPreviewResult] = useState<{ total: number; results: Movement[] } | null>(null)
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [applyLoading, setApplyLoading] = useState(false)
    const [applyResult, setApplyResult] = useState<string | null>(null)

    useEffect(() => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current)
        if (!validateTree(conditionTree as TreeNode, isLeafValid)) {
            return
        }
        setPreviewLoading(true)
        debounceTimer.current = setTimeout(() => {
            API.post('/classification-rules/preview', { condition: conditionTree })
                .then(response => setPreviewResult(response.data as { total: number; results: Movement[] }))
                .catch(() => setPreviewResult(null))
                .finally(() => setPreviewLoading(false))
        }, 500)
        return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current) }
    }, [conditionTree]) // eslint-disable-line react-hooks/exhaustive-deps

    const schema = yup.object().shape({ name: yup.string().trim().required() })
    const initialValues = { name: previousValues?.name ?? '' }

    const handleSubmit = (values: { name: string }) => {
        if (!validateTree(conditionTree as TreeNode, isLeafValid)) {
            setShowErrors(true)
            return
        }
        completion({ name: values.name, condition: conditionTree, tagIds })
    }

    const canApply = validateTree(conditionTree as TreeNode, isLeafValid) && tagIds.length > 0

    const handleApply = () => {
        if (!canApply) return
        setApplyLoading(true)
        setApplyResult(null)
        API.post('/classification-rules/apply', { condition: conditionTree, tagIds })
            .then(response => {
                const count = (response.data as any).updatedCount as number
                setApplyResult(`Tags applied to ${count} movement${count === 1 ? '' : 's'}.`)
            })
            .catch(() => setApplyResult('Failed to apply tags.'))
            .finally(() => setApplyLoading(false))
    }

    return (
        <Formik validationSchema={schema} onSubmit={handleSubmit} initialValues={initialValues}>
            {({ handleSubmit: formikSubmit, handleChange, values, errors }) => (
                <Form noValidate onSubmit={formikSubmit}>
                    <Form.Group controlId="formName" className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            placeholder="Rule name"
                            value={values.name}
                            onChange={handleChange}
                            isInvalid={!!errors.name}
                        />
                        <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Label>Conditions</Form.Label>
                    <div className="mb-3">
                        <ConditionTreeEditor
                            node={conditionTree as TreeNode}
                            onChange={n => setConditionTree(n as ConditionNode)}
                            renderLeaf={renderLeaf}
                            defaultLeaf={defaultLeaf}
                            depth={0}
                            showErrors={showErrors}
                        />
                    </div>

                    <div className="mb-4 p-3 border rounded bg-light">
                        <div className="fw-semibold mb-2">
                            Matching movements preview
                            {previewLoading && <Spinner animation="border" size="sm" className="ms-2" />}
                        </div>
                        {!previewLoading && previewResult === null && (
                            <span className="text-muted small">Complete all condition parameters to see a preview.</span>
                        )}
                        {!previewLoading && previewResult !== null && previewResult.total === 0 && (
                            <span className="text-muted small">No existing movements match this rule.</span>
                        )}
                        {!previewLoading && previewResult !== null && previewResult.total > 0 && (
                            <>
                                <div className="text-muted small mb-2">
                                    {previewResult.total > previewResult.results.length
                                        ? `Showing ${previewResult.results.length} of ${previewResult.total} matches`
                                        : `${previewResult.total} movement${previewResult.total === 1 ? '' : 's'} match`}
                                </div>
                                <Table size="sm" hover className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Description</th>
                                            <th className="text-end">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewResult.results.map(m => (
                                            <tr key={m.id}>
                                                <td className="text-nowrap">{m.date}</td>
                                                <td>{m.description ?? m.statementEntry}</td>
                                                <td className="text-end text-nowrap">{m.amount}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </>
                        )}
                    </div>

                    <Form.Group controlId="formTags" className="mb-3">
                        <Form.Label>Tags to assign</Form.Label>
                        <TagSelect
                            selectedTagIds={tagIds}
                            availableTags={availableTags}
                            onChange={(ids, updatedTags) => {
                                setTagIds(ids)
                                setAvailableTags(updatedTags)
                            }}
                        />
                    </Form.Group>

                    <div className="d-flex align-items-center gap-2">
                        <Button variant="primary" type="submit">Save</Button>
                        <Button
                            variant="outline-secondary"
                            disabled={!canApply || applyLoading}
                            onClick={handleApply}
                        >
                            {applyLoading
                                ? <><Spinner animation="border" size="sm" className="me-1" />Applying…</>
                                : 'Apply tags to all matching movements'}
                        </Button>
                        {applyResult && <span className="text-muted small">{applyResult}</span>}
                    </div>
                </Form>
            )}
        </Formik>
    )
}

function EditClassificationRuleWorkflow(props: Props) {
    const [redirect, setRedirect] = useState<string | null>(null)

    if (redirect) {
        return <Navigate to={redirect} replace />
    }

    const loadingStep = (_parameters: unknown) => {
        const dataToFetch: Promise<any>[] = [
            API.get('/tags').then(response => (response.data as any)["results"])
        ]
        if (props.entryId !== undefined) {
            dataToFetch.push(
                API.get(`/classification-rules/${props.entryId}`).then(response => response.data)
            )
        }
        return Promise.all(dataToFetch).then(arr => ({ tags: arr[0], rule: arr[1] || null }))
    }

    const userInputStep = (_parameters: unknown, loading_result: unknown, _values: unknown, _errors: unknown, completion: (input: unknown) => void) => {
        const lr = loading_result as any
        return {
            component: (
                <ClassificationRuleForm
                    loadedTags={lr.tags}
                    previousValues={lr.rule}
                    completion={completion}
                />
            ),
            title: props.entryId ? 'Edit rule' : 'New rule'
        }
    }

    const executionStep = (_parameters: unknown, _loading_result: unknown, user_input: unknown) => {
        if (props.entryId) {
            return API.put(`/classification-rules/${props.entryId}`, user_input)
        } else {
            return API.post('/classification-rules', user_input)
        }
    }

    const completionCallback = (result: unknown) => {
        if (Types.isString(props.onSuccess)) {
            setRedirect(props.onSuccess as string)
        } else {
            (props.onSuccess as (result: unknown) => void)(result)
        }
    }

    return <OperationWorkflowModal
        loadingStep={loadingStep}
        userInputStep={userInputStep}
        executionStep={executionStep}
        completionCallback={completionCallback}
        static={props.static}
    />
}

export default EditClassificationRuleWorkflow

